import { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';

import { api, ApiError } from '../api';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { Notice } from '../components/Notice';
import { PageHeader } from '../components/PageHeader';
import type { Device } from '../types';
import { formatBattery, formatRelative, isRelayReady } from '../types';

interface PairingChallenge { challengeId: string; qrPayload: string; expiresAt: string }
interface PairingStatus { status: 'pending' | 'completed' | 'expired'; expiresAt: string; deviceId?: string }

export function pairingStartRequest(): RequestInit {
  return { method: 'POST', body: JSON.stringify({ requesterLabel: 'Messavo web inbox' }) };
}

export function relayUpdateRequest(enabled: boolean, priority: number, subscriptionFingerprint: string | null): RequestInit {
  return { method: 'PATCH', body: JSON.stringify({ enabled, priority, subscriptionFingerprint }) };
}

export function simChangeAcknowledgementRequest(): RequestInit {
  return { method: 'POST', body: JSON.stringify({ confirmation: 'ACKNOWLEDGE SIM CHANGE' }) };
}

export function DevicesPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(true);
  const [pairing, setPairing] = useState<PairingChallenge | null>(null);
  const [pairingQr, setPairingQr] = useState('');
  const [pairingExpired, setPairingExpired] = useState(false);
  const [pairingComplete, setPairingComplete] = useState(false);
  const mounted = useRef(true);
  const loadSequence = useRef(0);

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    try {
      const result = await api<{ devices: Device[] }>('/api/devices');
      if (!mounted.current || sequence !== loadSequence.current) return;
      setDevices(result.devices); setLoadError('');
    } catch {
      if (mounted.current && sequence === loadSequence.current) setLoadError('Could not load connected phones.');
    } finally {
      if (mounted.current && sequence === loadSequence.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
      loadSequence.current += 1;
    };
  }, [load]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    const interval = window.setInterval(refreshWhenVisible, 15_000);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [load]);

  useEffect(() => {
    if (!pairing || pairingComplete || pairingExpired) return;
    let stopped = false;
    const check = async () => {
      try {
        const result = await api<PairingStatus>(`/api/device/pairing/${pairing.challengeId}`);
        if (stopped) return;
        if (result.status === 'completed') {
          setPairingComplete(true);
          setSuccess('Phone connected. Finish the sync disclosure on the phone to use the web inbox.');
          await load();
        } else if (result.status === 'expired') setPairingExpired(true);
      } catch { /* Keep a still-valid code through a brief connection interruption. */ }
    };
    const interval = window.setInterval(() => { void check(); }, 2_000);
    void check();
    return () => { stopped = true; window.clearInterval(interval); };
  }, [load, pairing, pairingComplete, pairingExpired]);

  async function startPairing() {
    setBusy('pairing'); setError(''); setSuccess('');
    setPairing(null); setPairingQr(''); setPairingExpired(false); setPairingComplete(false);
    try {
      const result = await api<PairingChallenge>('/api/device/pairing/start', pairingStartRequest());
      const dataUrl = await QRCode.toDataURL(result.qrPayload, {
        errorCorrectionLevel: 'M', margin: 2, width: 320,
        color: { dark: '#17233c', light: '#ffffff' },
      });
      setPairing(result); setPairingQr(dataUrl);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create a secure connection code.');
    } finally { setBusy(''); }
  }

  function closePairing() {
    setPairing(null); setPairingQr(''); setPairingExpired(false); setPairingComplete(false);
  }

  async function revoke(device: Device) {
    if (!window.confirm(`Disconnect ${device.name}? It will stop syncing and sending web-queued messages.`)) return;
    setBusy(device.id); setError(''); setSuccess('');
    try { await api(`/api/devices/${device.id}`, { method: 'DELETE' }); await load(); }
    catch (caught) { setError(caught instanceof ApiError ? caught.message : 'Could not disconnect this phone.'); }
    finally { setBusy(''); }
  }

  async function saveRelay(device: Device, enabled: boolean, priority: number, subscriptionFingerprint: string | null) {
    setBusy(`relay:${device.id}`); setError(''); setSuccess('');
    try {
      await api(`/api/devices/${device.id}/relay`, relayUpdateRequest(enabled, priority, subscriptionFingerprint));
      setSuccess(`${device.name} relay settings saved.`); await load();
    } catch (caught) { setError(caught instanceof ApiError ? caught.message : 'Could not save relay settings.'); }
    finally { setBusy(''); }
  }

  async function acknowledgeSimChange(device: Device) {
    setBusy(`sim:${device.id}`); setError(''); setSuccess('');
    try {
      await api(`/api/devices/${device.id}/sim-change/acknowledge`, simChangeAcknowledgementRequest());
      setSuccess(`${device.name} SIM change acknowledged. Review its relay SIM before enabling sends.`); await load();
    } catch (caught) { setError(caught instanceof ApiError ? caught.message : 'Could not acknowledge this SIM change.'); }
    finally { setBusy(''); }
  }

  return <div className="page narrow-page">
    <PageHeader eyebrow="Phone connections" title="Connected phones" description="Connect Android phones, see their live status, and choose the order used by SMS relay and API sends." actions={<button className="button button-primary" type="button" disabled={busy === 'pairing'} onClick={() => void startPairing()}><Icon name="qr_code_2" /> {busy === 'pairing' ? 'Creating code…' : 'Connect a phone'}</button>} />
    {error && <Notice kind="error">{error}</Notice>}
    {loadError && <Notice kind="error">{loadError}</Notice>}
    {success && <Notice kind="success">{success}</Notice>}
    <Notice>Pairing requires the Messavo Android app and expires after five minutes. A phone cannot send from the web until you also accept its separate sync disclosure.</Notice>
    {loading ? <div className="skeleton-list" aria-label="Loading connected phones"><span /><span /></div>
      : devices.length === 0 ? <EmptyState icon="devices" title="No connected phones" body="Install Messavo on Android, then choose Connect a phone and scan the secure QR code." />
        : <div className="device-list">{devices.map((device) => <DeviceCard device={device} busy={busy} key={device.id} onAcknowledge={() => void acknowledgeSimChange(device)} onRevoke={() => void revoke(device)} onSave={(enabled, priority, fingerprint) => void saveRelay(device, enabled, priority, fingerprint)} />)}</div>}
    {pairing && <Modal title={pairingComplete ? 'Phone signed in' : 'Scan with Messavo'} description={pairingComplete ? 'The phone is signed in. Web sync needs separate consent on the phone.' : 'On the Android sign-in screen, choose Sign in with a QR code. Scan this code and confirm the account before signing in.'} onClose={closePairing}>
      <div className="pairing-content">
        {pairingComplete ? <div className="pairing-result"><span><Icon name="check_circle" size={40} /></span><p>You can close this window. The phone will appear in the list and can enable optional web sync.</p></div>
          : pairingExpired ? <><Notice kind="warning">This connection code expired without connecting a phone.</Notice><button className="button button-primary button-wide" type="button" onClick={() => void startPairing()}>Create a new code</button></>
            : <>{pairingQr && <img className="pairing-qr" src={pairingQr} alt="One-time QR code for connecting an Android phone" />}<p className="pairing-expiry">Keep this page open. Code expires {new Date(pairing.expiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.</p><Notice kind="warning">Only approve the connection if the phone shows your expected Messavo account.</Notice></>}
      </div>
    </Modal>}
  </div>;
}

function DeviceCard({ device, busy, onAcknowledge, onRevoke, onSave }: {
  device: Device; busy: string; onAcknowledge(): void; onRevoke(): void;
  onSave(enabled: boolean, priority: number, subscriptionFingerprint: string | null): void;
}) {
  const [enabled, setEnabled] = useState(Boolean(device.relayEnabled));
  const [priority, setPriority] = useState(device.relayPriority ?? 100);
  const [fingerprint, setFingerprint] = useState(device.relaySubscriptionFingerprint ?? '');
  useEffect(() => {
    setEnabled(Boolean(device.relayEnabled)); setPriority(device.relayPriority ?? 100);
    setFingerprint(device.relaySubscriptionFingerprint ?? '');
  }, [device.relayEnabled, device.relayPriority, device.relaySubscriptionFingerprint]);

  const disconnected = Boolean(device.revoked_at);
  const relayBusy = busy === `relay:${device.id}`;
  const relayReady = isRelayReady(device);
  return <article className={`device-card ${disconnected ? 'muted' : ''}`}>
    <div className="device-summary">
      <span className="device-icon"><Icon name="phone_android" size={28} /></span>
      <div>
        <span className="device-title-line"><h2>{device.name}</h2>{!disconnected && <span className={`status-pill ${relayReady ? 'status-ready' : ''}`}>{relayReady ? 'Relay ready' : device.relayIssue?.replaceAll('_', ' ') || 'Offline'}</span>}</span>
        <p>{disconnected ? 'Disconnected' : (device.sync_enabled ?? device.syncEnabled) ? 'Web sync enabled' : 'Connected · sync not enabled'}</p>
        <small>Last seen {formatRelative(device.last_seen_at)} · Push {device.push_registered ? 'ready' : 'not ready'}</small>
        {!disconnected && <small><Icon name={device.batteryCharging ? 'battery_charging_full' : 'battery_horiz_050'} size={16} /> {formatBattery(device.batteryLevel, device.batteryCharging)}{device.batteryUpdatedAt ? ` · updated ${formatRelative(device.batteryUpdatedAt)}` : ''}</small>}
      </div>
      {!disconnected && <button className="button button-quiet danger" disabled={busy === device.id} type="button" onClick={onRevoke}>Disconnect</button>}
    </div>
    {!disconnected && <div className="device-details">
      <section className="sim-panel" aria-labelledby={`sim-title-${device.id}`}>
        <h3 id={`sim-title-${device.id}`}>SIMs on this phone</h3>
        {device.subscriptions?.length ? <div className="sim-list">{device.subscriptions.map((subscription) => <span key={subscription.id}><Icon name="sim_card" size={18} /><span><b>SIM {subscription.slotIndex + 1} · {subscription.displayName}</b><small>{subscription.carrierName || 'Carrier unavailable'}{subscription.id === device.default_sms_subscription_id ? ' · Android default' : ''}</small></span></span>)}</div> : <p>No active SIM details reported yet.</p>}
        {device.simChangePending && <div className="sim-warning"><Notice kind="warning">A SIM or eSIM changed{device.simChangedAt ? ` ${formatRelative(device.simChangedAt)}` : ''}. Relay sends are paused until you confirm this phone&apos;s current SIMs.</Notice><button className="button button-tonal" type="button" disabled={busy === `sim:${device.id}`} onClick={onAcknowledge}>I reviewed this SIM change</button></div>}
      </section>
      <form className="relay-panel" onSubmit={(event) => { event.preventDefault(); onSave(enabled, priority, fingerprint || null); }}>
        <div><h3>SMS relay and API fallback</h3><p>Messavo uses an integration&apos;s original phone first when it is ready, then lower priority numbers first. A claimed or uncertain send never moves to another phone.</p></div>
        <label className="toggle-row"><input type="checkbox" checked={enabled} disabled={device.simChangePending} onChange={(event) => setEnabled(event.target.checked)} /><span><b>Use this phone for relay sends</b><small>{device.simChangePending ? 'Review the SIM change first.' : 'This includes authorized API sends.'}</small></span></label>
        <div className="relay-fields">
          <label>Fallback priority<input type="number" min="0" max="1000" step="1" required value={priority} onChange={(event) => setPriority(Number(event.target.value))} /><small>0 is first; 1000 is last.</small></label>
          <label>SIM for relay<select value={fingerprint} onChange={(event) => setFingerprint(event.target.value)}><option value="">Automatic SIM</option>{device.subscriptions?.map((subscription) => subscription.identityFingerprint && <option key={subscription.identityFingerprint} value={subscription.identityFingerprint}>SIM {subscription.slotIndex + 1} · {subscription.displayName}</option>)}</select><small>Automatic follows the phone&apos;s current default. A chosen SIM never silently switches.</small></label>
        </div>
        <button className="button button-tonal" type="submit" disabled={relayBusy || (enabled && Boolean(device.simChangePending))}>{relayBusy ? 'Saving…' : 'Save relay settings'}</button>
      </form>
    </div>}
  </article>;
}
